import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, fmtMoney, downloadWithToast } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash, FilePdf, FloppyDisk, UsersThree, CaretDown, Sparkle } from "@phosphor-icons/react";

let _k = 0;
const nextKey = () => `pl-${Date.now().toString(36)}-${(++_k).toString(36)}`;
const blankLine = () => ({ _key: nextKey(), model: "", description: "", hsn: "8415", unit: "Nos", qty: 1, rate: 0, discount_pct: 0, gst_pct: 18 });

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// Daikin model picker — type to search the full price catalog; picking a model
// fills description, HSN, GST and the Daikin list price automatically.
function DaikinModelCell({ value, catalog, onText, onPick }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const q = (value || "").trim().toLowerCase();

  const results = useMemo(() => {
    if (!catalog?.length) return [];
    const list = q
      ? catalog.filter((it) => `${it.model} ${it.description} ${it.category}`.toLowerCase().includes(q))
      : catalog;
    return list.slice(0, 40);
  }, [catalog, q]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <Input
        value={value}
        onChange={(e) => { onText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search Daikin…"
        autoComplete="off"
        className="h-7 text-[11.5px]"
        data-testid="pi-line-model"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 left-0 w-[460px] max-h-72 overflow-auto rounded-md border border-slate-200 bg-white shadow-xl" data-testid="pi-model-dropdown">
          <div className="px-3 py-1 text-[9px] font-semibold uppercase tracking-widest text-slate-400 bg-slate-50 sticky top-0">
            Daikin catalog · {results.length}{catalog.length > results.length ? `/${catalog.length}` : ""}
          </div>
          {results.map((it) => (
            <button
              type="button"
              key={it.id || it.model}
              onMouseDown={(e) => { e.preventDefault(); onPick(it); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 hover:bg-orange-50 border-b border-slate-50 last:border-0 flex items-start justify-between gap-3"
              data-testid={`pi-model-opt-${it.model}`}
            >
              <div className="min-w-0">
                <div className="font-mono text-[11.5px] font-semibold text-slate-900">{it.model}</div>
                <div className="text-[10px] text-slate-500 truncate">{it.description || it.category}</div>
              </div>
              <div className="text-[10.5px] font-mono-num text-[#EA580C] whitespace-nowrap">{fmtMoney(it.daikin_list_price || 0)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProformaEditorSheet({ piId, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [piNo, setPiNo] = useState(null);
  const [status, setStatus] = useState("draft");
  const [states, setStates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [custOpen, setCustOpen] = useState(false);

  const [date, setDate] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [subject, setSubject] = useState("Proforma Invoice for Daikin VRV System");
  const [buyer, setBuyer] = useState({ name: "", address: "", gstin: "", state: "", contact: "", email: "" });
  const [shipTo, setShipTo] = useState({ name: "", address: "", gstin: "", state: "", contact: "", email: "" });
  const [items, setItems] = useState([blankLine()]);
  const [catalog, setCatalog] = useState([]);
  const [bank, setBank] = useState({ bank_name: "", account_name: "", account_no: "", ifsc: "", branch: "" });
  const [signatory, setSignatory] = useState("");
  const [terms, setTerms] = useState([]);
  const [notes, setNotes] = useState("");
  const [saveCustomer, setSaveCustomer] = useState(true);
  const [extractOpen, setExtractOpen] = useState(false);
  const [extractFile, setExtractFile] = useState(null);
  const [extractText, setExtractText] = useState("");
  const [extracting, setExtracting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: meta }, { data: custs }, settingsRes, catRes] = await Promise.all([
        api.get("/proforma/meta"),
        api.get("/customers"),
        api.get("/proforma/settings"),
        api.get("/price-list", { params: { active_only: true, limit: 1000 } }),
      ]);
      setStates(meta.states || []);
      setCustomers(custs || []);
      setCatalog(catRes.data || []);
      const s = settingsRes.data || {};
      if (piId) {
        const { data: pi } = await api.get(`/proforma-invoices/${piId}`);
        setPiNo(pi.pi_no); setStatus(pi.status);
        setDate(pi.date || ""); setValidUntil(pi.valid_until || "");
        setSubject(pi.subject || "");
        setBuyer({ name: "", address: "", gstin: "", state: "", contact: "", email: "", ...(pi.buyer || {}) });
        setShipTo({ name: "", address: "", gstin: "", state: "", contact: "", email: "", ...(pi.ship_to || {}) });
        setItems((pi.items || []).map((it) => ({ _key: nextKey(), ...it })));
        setBank({ bank_name: "", account_name: "", account_no: "", ifsc: "", branch: "", ...(pi.bank || {}) });
        setSignatory(pi.signatory_name || "");
        setTerms(pi.terms || []); setNotes(pi.notes || "");
        setSaveCustomer(false);
      } else {
        const iso = (d) => d.toISOString().slice(0, 10);
        const t = new Date();
        const vu = new Date(t); vu.setDate(vu.getDate() + 15);
        setDate(iso(t)); setValidUntil(iso(vu));
        setTerms(s.default_terms || meta.default_terms || []);
        setNotes(s.default_notes || "");
        setBank({ bank_name: "", account_name: "", account_no: "", ifsc: "", branch: "", ...(s.bank || {}) });
        setSignatory(s.signatory_name || "");
      }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [piId]);

  useEffect(() => { load(); }, [load]);

  const pickCustomer = (c) => {
    setBuyer({ name: c.name || "", address: c.address || "", gstin: c.gstin || "", state: c.state || "", contact: c.contact || "", email: c.email || "" });
    setSaveCustomer(false);
    setCustOpen(false);
  };

  const copyBuyerToShip = () => setShipTo({ ...buyer });

  const runExtract = async () => {
    if (!extractFile && !extractText.trim()) return toast.error("Upload a file or paste some text");
    setExtracting(true);
    try {
      const fd = new FormData();
      if (extractFile) fd.append("file", extractFile);
      if (extractText.trim()) fd.append("text", extractText.trim());
      const { data } = await api.post("/proforma/extract", fd);
      const blank = { name: "", address: "", gstin: "", state: "", contact: "", email: "" };
      if (data.subject) setSubject(data.subject);
      if (data.buyer && (data.buyer.name || data.buyer.gstin || data.buyer.address)) setBuyer({ ...blank, ...data.buyer });
      if (data.ship_to && (data.ship_to.name || data.ship_to.gstin || data.ship_to.address)) setShipTo({ ...blank, ...data.ship_to });
      if (data.items?.length) setItems(data.items.map((it) => ({ _key: nextKey(), ...it })));
      toast.success("Details extracted — please review everything before saving");
      setExtractOpen(false); setExtractFile(null); setExtractText("");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setExtracting(false); }
  };

  const setLine = (key, patch) => setItems((prev) => prev.map((l) => (l._key === key ? { ...l, ...patch } : l)));
  const addLine = () => setItems((prev) => [...prev, blankLine()]);
  const delLine = (key) => setItems((prev) => prev.filter((l) => l._key !== key));

  // Live totals + intra/inter preview (mirrors backend logic)
  const calc = useMemo(() => {
    const scode = (() => {
      const g = (buyer.gstin || "").trim();
      if (g.length >= 2 && /^\d{2}/.test(g)) return g.slice(0, 2);
      return "";
    })();
    // Seller is Haryana (06); if buyer state unknown, default intra.
    const intra = scode ? scode === "06" : (buyer.state ? buyer.state.toLowerCase() === "haryana" : true);
    let taxable = 0, tax = 0;
    items.forEach((l) => {
      const amt = (Number(l.qty) || 0) * (Number(l.rate) || 0) * (1 - (Number(l.discount_pct) || 0) / 100);
      taxable += amt;
      tax += amt * (Number(l.gst_pct) || 0) / 100;
    });
    const grand = Math.round(taxable + tax);
    return { intra, taxable, tax, grand };
  }, [items, buyer.gstin, buyer.state]);

  const buildPayload = () => ({
    date: date || undefined,
    valid_until: validUntil || undefined,
    subject,
    buyer,
    ship_to: shipTo,
    items: items.map(({ _key, ...rest }) => ({ ...rest, qty: Number(rest.qty) || 0, rate: Number(rest.rate) || 0, discount_pct: Number(rest.discount_pct) || 0, gst_pct: Number(rest.gst_pct) || 0 })),
    bank,
    signatory_name: signatory,
    terms,
    notes,
    save_customer: saveCustomer,
  });

  const save = async (thenPdf = false) => {
    if (!buyer.name.trim()) return toast.error("Enter the buyer name");
    if (!items.some((l) => (l.model || l.description) && Number(l.qty) > 0)) return toast.error("Add at least one line item with a quantity");
    setBusy(true);
    try {
      let id = piId;
      let no = piNo;
      if (piId) {
        await api.put(`/proforma-invoices/${piId}`, buildPayload());
      } else {
        const { data } = await api.post("/proforma-invoices", buildPayload());
        id = data.id; no = data.pi_no; setPiNo(data.pi_no);
      }
      toast.success(piId ? "Proforma Invoice updated" : "Proforma Invoice created");
      if (thenPdf && id) await downloadWithToast(`/proforma-invoices/${id}/pdf`, `${(no || "proforma").replace(/\//g, "-")}.pdf`).catch(() => {});
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto p-0" data-testid="pi-editor-sheet">
        <SheetHeader className="px-5 py-3 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="text-[15px] flex items-center gap-2">
              {piId ? `Proforma Invoice ${piNo || ""}` : "New Proforma Invoice"}
              {piId && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600">{status}</span>}
            </SheetTitle>
            <Button variant="outline" size="sm" className="h-8 text-[11.5px] border-[#EA580C] text-[#EA580C] hover:bg-orange-50" onClick={() => setExtractOpen(true)} data-testid="pi-autofill-btn">
              <Sparkle size={13} className="mr-1" weight="fill" /> Auto-fill from document
            </Button>
          </div>
        </SheetHeader>

        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
        ) : (
          <div className="px-5 py-4 space-y-5">
            {/* Meta */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="PI Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 text-[12px]" data-testid="pi-date" /></Field>
              <Field label="Valid Until"><Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="h-8 text-[12px]" data-testid="pi-valid-until" /></Field>
              <Field label="Subject" className="col-span-2 md:col-span-2"><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-8 text-[12px]" data-testid="pi-subject" /></Field>
            </div>

            {/* Buyer */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-[#EA580C]">Buyer (Bill To)</div>
                <Popover open={custOpen} onOpenChange={setCustOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="pi-pick-customer">
                      <UsersThree size={12} className="mr-1" /> Pick saved customer <CaretDown size={10} className="ml-1" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="end">
                    <Command>
                      <CommandInput placeholder="Search customers…" className="text-[12px]" />
                      <CommandList>
                        <CommandEmpty>No saved customers.</CommandEmpty>
                        <CommandGroup>
                          {customers.map((c) => (
                            <CommandItem key={c.id} value={`${c.name} ${c.gstin}`} onSelect={() => pickCustomer(c)} className="text-[12px]" data-testid={`pi-cust-${c.id}`}>
                              <div>
                                <div className="font-medium">{c.name}</div>
                                <div className="text-[10px] text-slate-500">{c.gstin || "No GSTIN"} · {c.state || "—"}</div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Buyer Name" className="col-span-2"><Input value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} className="h-8 text-[12px]" data-testid="pi-buyer-name" /></Field>
                <Field label="GSTIN"><Input value={buyer.gstin} onChange={(e) => setBuyer({ ...buyer, gstin: e.target.value.toUpperCase() })} placeholder="06ABCDE1234A1Z5" className="h-8 text-[12px] font-mono" data-testid="pi-buyer-gstin" /></Field>
                <Field label="State">
                  <select value={buyer.state} onChange={(e) => setBuyer({ ...buyer, state: e.target.value })} className="h-8 text-[12px] w-full border border-slate-200 rounded-sm px-2" data-testid="pi-buyer-state">
                    <option value="">Select…</option>
                    {states.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Address" className="col-span-2"><Input value={buyer.address} onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} className="h-8 text-[12px]" data-testid="pi-buyer-address" /></Field>
                <Field label="Contact"><Input value={buyer.contact} onChange={(e) => setBuyer({ ...buyer, contact: e.target.value })} className="h-8 text-[12px]" data-testid="pi-buyer-contact" /></Field>
                <Field label="Email"><Input value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} className="h-8 text-[12px]" data-testid="pi-buyer-email" /></Field>
              </div>
              <label className="flex items-center gap-2 mt-2 text-[11px] text-slate-600 cursor-pointer">
                <Checkbox checked={saveCustomer} onCheckedChange={(v) => setSaveCustomer(!!v)} data-testid="pi-save-customer" />
                Save this buyer to the customer list for reuse
              </label>
            </div>

            {/* Ship-to */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-[#EA580C]">Ship To (Delivery Address)</div>
                <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={copyBuyerToShip} data-testid="pi-ship-copy">Same as buyer</Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Consignee Name" className="col-span-2"><Input value={shipTo.name} onChange={(e) => setShipTo({ ...shipTo, name: e.target.value })} className="h-8 text-[12px]" data-testid="pi-ship-name" /></Field>
                <Field label="GSTIN"><Input value={shipTo.gstin} onChange={(e) => setShipTo({ ...shipTo, gstin: e.target.value.toUpperCase() })} placeholder="27ABCDE1234A1Z5" className="h-8 text-[12px] font-mono" data-testid="pi-ship-gstin" /></Field>
                <Field label="State">
                  <select value={shipTo.state} onChange={(e) => setShipTo({ ...shipTo, state: e.target.value })} className="h-8 text-[12px] w-full border border-slate-200 rounded-sm px-2" data-testid="pi-ship-state">
                    <option value="">Select…</option>
                    {states.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Delivery Address" className="col-span-2 md:col-span-4"><Input value={shipTo.address} onChange={(e) => setShipTo({ ...shipTo, address: e.target.value })} className="h-8 text-[12px]" data-testid="pi-ship-address" /></Field>
              </div>
            </div>

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-[#EA580C]">VRV Line Items (agreed rates, ex-GST)</div>
                <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={addLine} data-testid="pi-add-line"><Plus size={12} className="mr-1" /> Add line</Button>
              </div>
              <div className="overflow-x-auto border border-slate-200 rounded-sm">
                <Table className="text-[11.5px] min-w-[820px]">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-36">Model</TableHead>
                      <TableHead className="min-w-40">Description</TableHead>
                      <TableHead className="w-20">HSN</TableHead>
                      <TableHead className="w-16 text-right">Qty</TableHead>
                      <TableHead className="w-32 text-right">Rate</TableHead>
                      <TableHead className="w-16 text-right">Disc%</TableHead>
                      <TableHead className="w-16 text-right">GST%</TableHead>
                      <TableHead className="w-28 text-right">Amount</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((l) => {
                      const amt = (Number(l.qty) || 0) * (Number(l.rate) || 0) * (1 - (Number(l.discount_pct) || 0) / 100);
                      return (
                        <TableRow key={l._key} data-testid="pi-line-row">
                          <TableCell><DaikinModelCell value={l.model} catalog={catalog} onText={(v) => setLine(l._key, { model: v })} onPick={(it) => setLine(l._key, { model: it.model, description: it.description || l.description, hsn: it.hsn || l.hsn, rate: it.daikin_list_price ?? l.rate, gst_pct: it.gst_pct ?? l.gst_pct })} /></TableCell>
                          <TableCell><Input value={l.description} onChange={(e) => setLine(l._key, { description: e.target.value })} placeholder="VRV IV 8HP Outdoor" className="h-7 text-[11.5px]" /></TableCell>
                          <TableCell><Input value={l.hsn} onChange={(e) => setLine(l._key, { hsn: e.target.value })} className="h-7 text-[11.5px]" /></TableCell>
                          <TableCell><Input type="number" value={l.qty} onChange={(e) => setLine(l._key, { qty: e.target.value })} className="h-7 text-[11.5px] text-right" data-testid="pi-line-qty" /></TableCell>
                          <TableCell><Input type="number" value={l.rate} onChange={(e) => setLine(l._key, { rate: e.target.value })} className="h-7 text-[11.5px] text-right" data-testid="pi-line-rate" /></TableCell>
                          <TableCell><Input type="number" value={l.discount_pct} onChange={(e) => setLine(l._key, { discount_pct: e.target.value })} className="h-7 text-[11.5px] text-right" /></TableCell>
                          <TableCell><Input type="number" value={l.gst_pct} onChange={(e) => setLine(l._key, { gst_pct: e.target.value })} className="h-7 text-[11.5px] text-right" /></TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(amt)}</TableCell>
                          <TableCell><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => delLine(l._key)}><Trash size={12} /></Button></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Totals preview */}
            <div className="flex justify-end">
              <div className="w-72 border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-1 text-[12px]">
                <div className="flex justify-between"><span className="text-slate-500">Taxable Value</span><span className="font-mono-num">{fmtMoney(calc.taxable)}</span></div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{calc.intra ? "CGST + SGST" : "IGST"}</span>
                  <span className="font-mono-num">{fmtMoney(calc.tax)}</span>
                </div>
                <div className="text-[9.5px] text-slate-400" data-testid="pi-tax-mode">{calc.intra ? "Intra-state supply (CGST + SGST)" : "Inter-state supply (IGST)"}</div>
                <div className="flex justify-between border-t pt-1 mt-1 text-[14px] font-bold text-[#EA580C]"><span>Grand Total</span><span className="font-mono-num" data-testid="pi-grand-total">{fmtMoney(calc.grand)}</span></div>
              </div>
            </div>

            {/* Bank + signatory */}
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-[#EA580C] mb-2">Bank Details (for advance payment)</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Account Name"><Input value={bank.account_name} onChange={(e) => setBank({ ...bank, account_name: e.target.value })} className="h-8 text-[12px]" data-testid="pi-bank-acname" /></Field>
                <Field label="Bank Name"><Input value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} className="h-8 text-[12px]" /></Field>
                <Field label="Account No."><Input value={bank.account_no} onChange={(e) => setBank({ ...bank, account_no: e.target.value })} className="h-8 text-[12px]" data-testid="pi-bank-acno" /></Field>
                <Field label="IFSC"><Input value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })} className="h-8 text-[12px] font-mono" /></Field>
                <Field label="Branch"><Input value={bank.branch} onChange={(e) => setBank({ ...bank, branch: e.target.value })} className="h-8 text-[12px]" /></Field>
                <Field label="Authorised Signatory"><Input value={signatory} onChange={(e) => setSignatory(e.target.value)} placeholder="Name" className="h-8 text-[12px]" data-testid="pi-signatory" /></Field>
              </div>
            </div>

            {/* Terms + notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Terms & Conditions (one per line)">
                <Textarea value={(terms || []).join("\n")} onChange={(e) => setTerms(e.target.value.split("\n"))} rows={6} className="text-[11.5px]" data-testid="pi-terms" />
              </Field>
              <Field label="Notes">
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={6} className="text-[11.5px]" data-testid="pi-notes" />
              </Field>
            </div>
          </div>
        )}

        <div className="px-5 py-3 border-t sticky bottom-0 bg-white flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="h-9 text-[12px]">Cancel</Button>
          <Button variant="outline" onClick={() => save(true)} disabled={busy || loading} className="h-9 text-[12px]" data-testid="pi-save-pdf">
            <FilePdf size={14} className="mr-1.5" /> Save & Download PDF
          </Button>
          <Button onClick={() => save(false)} disabled={busy || loading} className="h-9 text-[12px] bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="pi-save">
            <FloppyDisk size={14} className="mr-1.5" /> {busy ? "Saving…" : "Save"}
          </Button>
        </div>

        <Dialog open={extractOpen} onOpenChange={(v) => { if (!v && !extracting) setExtractOpen(false); }}>
          <DialogContent className="max-w-lg" data-testid="pi-extract-dialog">
            <DialogHeader>
              <DialogTitle className="text-[15px] flex items-center gap-2"><Sparkle size={16} weight="fill" className="text-[#EA580C]" /> Auto-fill from a document</DialogTitle>
              <DialogDescription className="text-[12px] text-slate-500">
                Upload a customer PO / enquiry / PI (PDF or photo) or paste the text. The AI reads the GST details, delivery address and equipment list, then fills the form for you to review.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Upload PDF or image</label>
                <input type="file" accept=".pdf,image/*" onChange={(e) => setExtractFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-[12px] file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-[#0F172A] file:text-white file:text-[11px] file:cursor-pointer" data-testid="pi-extract-file" />
                {extractFile && <div className="text-[11px] text-slate-500 mt-1 truncate">{extractFile.name}</div>}
              </div>
              <div className="text-center text-[10px] text-slate-400 tracking-widest">— OR —</div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Paste text / email</label>
                <Textarea value={extractText} onChange={(e) => setExtractText(e.target.value)} rows={5} placeholder="Paste the PO / enquiry details here…" className="mt-1 text-[12px]" data-testid="pi-extract-text" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setExtractOpen(false)} disabled={extracting} className="h-8 text-[12px]">Cancel</Button>
              <Button onClick={runExtract} disabled={extracting} className="h-8 text-[12px] bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="pi-extract-run">
                {extracting ? "Reading…" : "Extract & fill"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}
